import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleRepairService } from 'src/app/services/repair/vehicle-repair.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models/api-response.interface';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-vehicle-repair',
  templateUrl: './vehicle-repair.component.html',
  styleUrls: ['./vehicle-repair.component.scss']
})
export class VehicleRepairComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  vehicleRepairs: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    stock_number: '',
    order_date_from: '',
    order_date_to: '',
    complete_date_from: '',
    complete_date_to: '',
    status: ''
  };

  constructor(
    private _vehicleRepairService: VehicleRepairService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVehicleRepairs();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.type == "alaedo" ? vehicle.pictures[1] : vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  private loadVehicleRepairs(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;
    Object.keys(filters).forEach(key => {
      if (filters[key] === null || filters[key] === undefined || filters[key] === '') {
        delete filters[key];
      }
    });
    this.subscriptions.push(
      this._vehicleRepairService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.vehicleRepairs = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.vehicleRepairs = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading vehicle repairs:', error);
          this.vehicleRepairs = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    if (data.pagination) {
      this.paginationInfo = data.pagination;
    } else {
      this.paginationInfo = {
        currentPage: data.current_page || 1,
        perPage: data.per_page || 20,
        total: data.total || 0,
        lastPage: data.last_page || 1,
        firstPage: 1
      };
    }
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadVehicleRepairs(page, this.searchTerm, this.filters);
  }

  onSearch(): void {
    this.filters.search = this.searchTerm;
    this.loadVehicleRepairs(1, this.searchTerm, this.filters);
  }

  applyFilters(): void {
    this.loadVehicleRepairs(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      stock_number: '',
      order_date_from: '',
      order_date_to: '',
      complete_date_from: '',
      complete_date_to: '',
      status: ''
    };
    this.searchTerm = '';
    this.loadVehicleRepairs(1, '', {});
  }

  openAddVehicleRepairModal(): void {
    const modalElement = document.getElementById('kt_modal_add_edit_vehicle_repair');
    if (modalElement) {
      // Clear any existing data
      modalElement.setAttribute('data-repair-id', '');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditVehicleRepairModal(repair: any): void {
    const modalElement = document.getElementById('kt_modal_add_edit_vehicle_repair');
    if (modalElement) {
      // Set repair ID for edit mode
      modalElement.setAttribute('data-repair-id', repair._id || repair.id);
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteVehicleRepair(repair: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this vehicle repair record?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(repair);
      }
    });
  }

  private performDelete(repair: any): void {
    const repairId = repair._id || repair.id;
    this.subscriptions.push(
      this._vehicleRepairService.DeleteDataById(repairId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Vehicle repair record has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadVehicleRepairs(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete vehicle repair record.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting vehicle repair:', error);
          Swal.fire({
            title: 'Error!',
            text: 'An error occurred while deleting the vehicle repair record.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal
  onVehicleRepairAdded(): void {
    this.loadVehicleRepairs(this.currentPage, this.searchTerm, this.filters);
  }

  onVehicleRepairUpdated(): void {
    this.loadVehicleRepairs(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'badge-light-warning';
      case 'repaired':
        return 'badge-light-success';
      default:
        return 'badge-light-secondary';
    }
  }

  // Math utility for template
  get Math() {
    return Math;
  }
}
